import { parse as parseQueryString } from '@/utils/querystring.js'
import { type Collection, type Request, Response } from 'miragejs'
import { orderBy as _orderBy } from 'lodash-es'
import type { MockServer } from './server.js'
import {
  rand,
  randChanceBoolean,
  randNumber,
  randParagraph,
} from '@ngneat/falso'
import { queryParamToSortingState } from '@/utils/sorting.js'

export function sortable<T>(
  collection: Collection<T>,
  request: Request,
  accessors: Partial<Record<keyof T, (item: T) => unknown>> = {},
  defaultSort: string[] = ['id'],
): Collection<T> {
  const { sort = defaultSort } = parseQueryString(new URL(request.url))
  const sortParsed = queryParamToSortingState(sort)

  const iteratees = sortParsed.map((col) => {
    const key = col.id as keyof T

    return accessors[key] ?? ((item: T) => item[key])
  })
  const orders = sortParsed.map((col): 'asc' | 'desc' =>
    col.desc ? 'desc' : 'asc',
  )

  // _orderBy returns T[] — assign back to .models to preserve the Collection wrapper
  collection.models = _orderBy(collection.models, iteratees, orders)
  return collection
}

export class PaginationError extends Error {
  override name = 'PaginationError'
}

export function pageable<T>(collection: Collection<T>, request: Request) {
  const count = collection.length
  const pageNumber = parseInt(
    (request.queryParams['page[number]'] as string) ?? '1',
  )
  const pageSize = parseInt(
    (request.queryParams['page[size]'] as string) ?? '10',
  )

  const start = (pageNumber - 1) * pageSize
  const end = pageNumber * pageSize

  if (start > count) {
    throw new PaginationError("Page doesn't exist")
  }

  return {
    data: collection.slice(start, end),
    meta: {
      count,
      page: pageNumber,
      pages: Math.ceil(count / pageSize),
    },
  }
}

export function filterable<T extends Record<string, unknown>>(
  collection: Collection<T>,
  request: Request,
  matchers: Record<string, (item: T, value: string) => boolean> = {},
) {
  // we need to parse query params to handle arrays like "filter[tags]=tag1,tag2"
  const params = parseQueryString(new URL(request.url))

  const filterPattern = 'filter['
  const filters = Object.entries(params)
    .filter(([key]) => key.startsWith(filterPattern))
    .map(function ([key, value]) {
      // extract property names from JSON API-style filters:
      // "filter[property]=value" => ["property", "value"]
      const property = key.slice(filterPattern.length, -1)
      return [property, value] as const
    })

  return collection.filter((item) => {
    return filters.every(([key, value]) => {
      // if we have a custom matcher for a field, use that.
      // otherwise use basic equality check
      const matcher = matchers[key] ?? ((item, value) => item[key] === value)

      return Array.isArray(value)
        ? value.some((v) => matcher(item, v))
        : matcher(item, value)
    })
  })
}

/**
 * Converts a query param value to a string.
 * In case it's an array, it joins the values with a comma.
 * If parseJson is true, attempts to parse the value as JSON and returns the parsed object.
 *
 * @param param - The query parameter value from request.queryParams
 * @returns The string value, or parsed JSON object if parseJson is true and parsing succeeds
 *
 * @example
 * // Basic usage - returns string value
 * const clientId = getQueryParamValue(request.queryParams['filter[client]'])
 * // Returns: "123"
 */
export function getQueryParamValue(
  param: Request['queryParams'][string] | unknown,
) {
  return param?.toString()
}

/**
 * Checks whether a JSON:API `include` query parameter contains a given
 * relationship prefix (e.g. "patients", "phone").
 */
export function includeRequested(
  includeParam: undefined | string | string[],
  prefix: string,
): boolean {
  if (!includeParam) {
    return false
  }
  const items = Array.isArray(includeParam)
    ? includeParam
    : includeParam.split(',').map((i) => i.trim())
  return items.some((i) => i.startsWith(prefix))
}

/**
 * Creates a Response object with error information following the JSON:API specification.
 * Returns an error object in the format: { errors: [{ code, detail, source, status }] }
 *
 * @example
 * // Single error
 * createErrorResponse({
 *   status: 400,
 *   headers = {},
 *   errors: [{
 *     code: 'invalid_choice',
 *     detail: `Select a valid choice. ${value} is not one of the available choices.`,
 *     pointer: '/data/attributes/status'
 *   }]
 * })
 */
export function createErrorResponse({
  status,
  headers = {},
  errors,
}: {
  errors: Array<{
    code?: string
    detail: string
    pointer?: string
    status?: string
  }>
  headers?: Record<string, string>
  status: number
}) {
  return new Response(status, headers, {
    errors: errors.map((error) => ({
      ...error,
      source: error.pointer ? { pointer: error.pointer } : undefined,
      status: error.status ?? status.toString(),
    })),
  })
}

/**
 * Generate a random financial amount with min/max parameters and decimal places
 */
export const generateFinanceAmount = ({
  min,
  max,
  dec = 2,
}: {
  dec?: number
  max: number
  min: number
}): string => {
  const amount = randNumber({ max, min, precision: dec })
  return amount.toFixed(dec)
}

/**
 * Generates a random time string in the format "HH:MM".
 */
export const generateRandomTimeString = () => {
  const hours = randNumber({ min: 0, max: 23 }).toString().padStart(2, '0')
  const minutes = randNumber({ min: 0, max: 59 }).toString().padStart(2, '0')

  return `${hours}:${minutes}`
}

/**
 * Returns a new array with the items in the array shuffled randomly.
 */
export const shuffleArrayElements = <T>(array: T[]): T[] => {
  const result = [...array]

  for (let currentIndex = result.length - 1; currentIndex > 0; currentIndex--) {
    // pick a random index between 0 and currentIndex
    const randomIndex = randNumber({ min: 0, max: currentIndex })

    // swap current item with the random one
    const temp = result[currentIndex]
    result[currentIndex] = result[randomIndex]
    result[randomIndex] = temp
  }

  return result
}

/*
 * Define random traits based on the given probabilities.
 *
 * @param traits - An object with trait names as keys and their probabilities as values.
 * @returns An array of selected trait names.
 *
 * @example
 * const traits = {
 *   withRows: 1.0,
 *   withFinalization: 1.0,
 *   withNoPayments: 0.2,
 *   withPartialPayment: 0.2,
 *   withFullPayment: 0.6,
 * }
 * // Output: ['withRows', 'withFinalization', 'withFullPayment']
 */
export function defineRandomTraits(traits: Record<string, number>) {
  const selectedTraits: string[] = []

  // Evaluate each trait independently
  for (const [trait, chanceTrue] of Object.entries(traits)) {
    if (randChanceBoolean({ chanceTrue })) {
      selectedTraits.push(trait)
    }
  }

  return selectedTraits
}
/**
 * Creates an HTML paragraph with random text content
 */
export const createHtmlParagraph = () => `<p>${randParagraph()}</p>`

/**
 * Returns an existing entity from the server's schema, or creates a new one if none exists.
 *
 * @param server - The Mirage server instance
 * @param entityType - The type of entity to get or create
 * @returns A random existing entity or a new one
 */
export const getExistingEntity = (
  server: MockServer,
  entityType: Parameters<typeof server.schema.all>[0],
) => {
  const existingEntities = server.schema.all(entityType).models
  return existingEntities.length
    ? rand(existingEntities)
    : server.create(entityType)
}
